import type { ReactNode } from 'react';
import styles from './SortableTable.module.css';

export type SortDir = 'asc' | 'desc';

export type SortState<K extends string> = { key: K; dir: SortDir };

type Column<K extends string> = {
  key: K;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'right';
};

type Props<K extends string> = {
  columns: Column<K>[];
  sort: SortState<K>;
  onSort: (key: K) => void;
  children: ReactNode;
  empty?: ReactNode;
  isEmpty?: boolean;
};

export function SortableTable<K extends string>({
  columns,
  sort,
  onSort,
  children,
  empty,
  isEmpty,
}: Props<K>) {
  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col) => {
              const active = sort.key === col.key;
              const ariaSort = !col.sortable
                ? undefined
                : active
                  ? sort.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none';
              return (
                <th key={col.key} className={col.align === 'right' ? styles.thRight : undefined}>
                  {col.sortable ? (
                    <button
                      type="button"
                      className={`${styles.thBtn} ${active ? styles.thBtnActive : ''}`}
                      onClick={() => onSort(col.key)}
                      aria-sort={ariaSort}
                    >
                      {col.label}
                      <span className={styles.sortIcon} aria-hidden>
                        {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        {!isEmpty ? <tbody>{children}</tbody> : null}
      </table>
      {isEmpty ? empty : null}
    </div>
  );
}

export function compareSort<T>(
  a: T,
  b: T,
  pick: (row: T) => string | number,
  dir: SortDir,
): number {
  const av = pick(a);
  const bv = pick(b);
  let cmp = 0;
  if (typeof av === 'number' && typeof bv === 'number') {
    cmp = av - bv;
  } else {
    cmp = String(av).localeCompare(String(bv), 'nl');
  }
  return dir === 'asc' ? cmp : -cmp;
}

export function toggleSort<K extends string>(prev: SortState<K>, key: K): SortState<K> {
  if (prev.key === key) {
    return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: key === 'name' || key === 'source' ? 'asc' : 'desc' };
}
